import type { ListingsService } from './listings';

export interface SearchOption {
  displayName: string;
  ids: string[];
  key: string;
  strict: boolean;
  uxid: string;
}

export interface SearchOptionProp {
  ids: (string | undefined)[];
  key: string;
}

export function SetSearchOptions(this: ListingsService): void {
  const _addressOptions: SearchOption[] = [];
  const _modelOptions: SearchOption[] = [];
  const _seriesOptions: SearchOption[] = [];
  // const repOptions: SearchOption[] = [];
  const _uliKeyOptions: SearchOption[] = [];
  const _vlsOptions: SearchOption[] = [];
  const _villageOptions: SearchOption[] = [];
  // const otherOptions: SearchOption[] = [];

  for (const _key of this.filteringService.searchOptionProps() as SearchOptionProp[]) {
    switch (_key.key) {
      case 'address':
        {
          for (const _id of _key.ids) {
            const _listing = this.allListings().find((l) => l.id === _id);
            const _houseNumberRegex = /^\d+/;
            const _isListingAddress = _houseNumberRegex.exec(
              this.filteringService.search().split(' ')[0].trim(),
            );
            const _addressDisplay = _listing?.address.streetAddress.split(' ').splice(1).join(' ');
            const _vlsDisplay =
              (_listing?.vls ?? '') + '- ' + (_listing?.address.streetAddress ?? '');

            if ((!_addressDisplay && !_vlsDisplay) || !_listing?.id) {
              continue;
            }

            const _existingAddressOption = _addressOptions.find(
              (o) => o.displayName === _addressDisplay,
            );

            if (_addressDisplay && !_existingAddressOption) {
              _addressOptions.push({
                displayName: _addressDisplay,
                ids: [_listing.id],
                key: _key.key,
                strict: !!_isListingAddress,
                uxid: _addressDisplay.replaceAll(' ', '-').toLowerCase(),
              });
            } else if (_addressDisplay && _existingAddressOption) {
              _existingAddressOption.ids.push(_listing.id);
            }

            if (!_vlsDisplay || !_listing.id) {
              continue;
            }

            const _existingVlsOption = _vlsOptions.find((o) => o.displayName === _vlsDisplay);

            if (_vlsDisplay && !_existingVlsOption) {
              _vlsOptions.unshift({
                displayName: _vlsDisplay,
                ids: [_listing.id],
                key: _key.key,
                strict: true,
                uxid: _vlsDisplay.replaceAll(' ', '-').toLowerCase(),
              });
            }
          }
        }
        break;
      // case 'listingReps':
      //   {
      //     for (const id of key.ids) {
      //       const listing = this.allListings()?.find(l => l.id === id);
      //       let rep;
      //       for(const token of key.matchedTokens) {
      //         if (!token || !listing) { continue; }

      //         rep = listing?.listingReps?.find(r => (`${r?.firstName} ${r?.lastName}`).toLowerCase().includes(token.toLowerCase()));

      //         if (!rep) { continue; }

      //         const repName = rep ? `${rep?.lastName}, ${rep?.firstName}` : null;
      //         const repDisplay = repName;

      //         if (!repDisplay || !listing || !listing?.id) { continue; }

      //         const existingOption = repOptions.find((o: {displayName: string, ids: string[], key: string}) => o.displayName === repDisplay);

      //         if (repDisplay && !existingOption) {
      //           repOptions.unshift({ displayName: repDisplay, ids: [listing?.id], key: key.key, strict: false });
      //         } else if (repDisplay && existingOption) {
      //           repOptions.find((o: {displayName: string, ids: string[], key: string}) => o.displayName === repDisplay)?.ids.push(listing?.id);
      //         }
      //       }
      //     }
      //   }
      //   break;
      case 'modelTitle':
        {
          for (const _id of _key.ids) {
            const _listing = this.allListings().find((l) => l.id === _id);
            const _modelTitle = (_listing?.model?.title ?? '') + ' Model';

            if (!_modelTitle || !_listing?.id) {
              continue;
            }

            const _existingOption = _modelOptions.find((o) => o.displayName === _modelTitle);

            if (_modelTitle && !_existingOption) {
              _modelOptions.push({
                displayName: _modelTitle,
                ids: [_listing.id],
                key: _key.key,
                strict: false,
                uxid: _modelTitle.replaceAll(' ', '-').toLowerCase(),
              });
            } else if (_modelTitle && _existingOption) {
              _existingOption.ids.push(_listing.id);
            }
          }
        }
        break;
      case 'seriesTitle':
        {
          for (const _id of _key.ids) {
            const _listing = this.allListings().find((l) => l.id === _id);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- This condition is necessary until we update the logic to fit the new types package.
            const _seriesTitle = (_listing?.series?.title ?? '') + ' Series';

            if (!_seriesTitle || !_listing?.id) {
              continue;
            }

            const _existingOption = _seriesOptions.find((o) => o.displayName === _seriesTitle);

            if (_seriesTitle && !_existingOption) {
              _seriesOptions.push({
                displayName: _seriesTitle,
                ids: [_listing.id],
                key: _key.key,
                strict: false,
                uxid: _seriesTitle.replaceAll(' ', '-').toLowerCase(),
              });
            } else if (_seriesTitle && _existingOption) {
              _existingOption.ids.push(_listing.id);
            }
          }
        }
        break;
      case 'villageTitle':
        {
          for (const _id of _key.ids) {
            const _listing = this.allListings().find((l) => l.id === _id);
            const _title = _listing?.village.title;
            const _villageDisplay =
              _title && _title !== 'Middleton' ? 'The Village of ' + _title : _title;

            if (!_villageDisplay || !_listing?.id) {
              continue;
            }

            const _existingOption = _villageOptions.find((o) => o.displayName === _villageDisplay);

            if (_villageDisplay && !_existingOption) {
              _villageOptions.unshift({
                displayName: _villageDisplay,
                ids: [_listing.id],
                key: _key.key,
                strict: false,
                uxid: _villageDisplay.replaceAll(' ', '-').toLowerCase(),
              });
            } else if (_villageDisplay && _existingOption) {
              _existingOption.ids.push(_listing.id);
            }
          }
        }
        break;
      case 'uliKey':
        {
          for (const _id of _key.ids) {
            const _listing = this.allListings().find((l) => l.id === _id);
            const _uliKeyDisplay = 'ULI Key: ' + (_listing?.uliKey ?? '');

            if (!_uliKeyDisplay || !_listing?.id) {
              continue;
            }

            const _existingOption = _uliKeyOptions.find((o) => o.displayName === _uliKeyDisplay);

            if (_uliKeyDisplay && !_existingOption) {
              _uliKeyOptions.unshift({
                displayName: _uliKeyDisplay,
                ids: [_listing.id],
                key: _key.key,
                strict: true,
                uxid: _uliKeyDisplay.replaceAll(' ', '-').toLowerCase(),
              });
            } else if (_uliKeyDisplay && _existingOption) {
              continue;
            }
          }
        }
        break;
      case 'vls':
        {
          for (const _id of _key.ids) {
            const _listing = this.allListings().find((l) => l.id === _id);
            const _vlsDisplay =
              (_listing?.vls ?? '') + '- ' + (_listing?.address.streetAddress ?? '');

            if (!_vlsDisplay || !_listing?.id) {
              continue;
            }

            const _existingOption = _vlsOptions.find((o) => o.displayName === _vlsDisplay);

            if (_vlsDisplay && !_existingOption) {
              _vlsOptions.unshift({
                displayName: _vlsDisplay,
                ids: [_listing.id],
                key: _key.key,
                strict: true,
                uxid: _vlsDisplay.replaceAll(' ', '-').toLowerCase(),
              });
            } else if (_vlsDisplay && _existingOption) {
              continue;
            }
          }
        }
        break;
      default:
        // {
        //   for (const id of key.ids) {
        //     if (!id) { continue; }

        //     const listing = this.allListings()?.find(l => l.id === id);
        //     // eslint-disable-next-line @typescript-eslint/no-explicit-any
        //     const fieldValue = listing ? (listing as any)[key.key] : null;
        //     const _key = key.key.charAt(0).toUpperCase() + key.key.slice(1);
        //     const displayValue = `${_key}: ${fieldValue}`;

        //     if (!fieldValue || !listing || !listing?.id) { continue; }

        //     const existingOption = otherOptions.find((o: {displayName: string, ids: string[], key: string, strict: boolean}) => o.displayName === displayValue);

        //     if (displayValue && !existingOption) {
        //       otherOptions.push({ displayName: displayValue, ids: [id], key: key.key, strict: false });
        //     } else if (displayValue && existingOption) {
        //       continue;
        //     }
        //   }
        // }
        break;
    }
  }

  this.filteringService.searchOptions.set([
    ..._modelOptions,
    ..._seriesOptions,
    ..._villageOptions,
    ..._addressOptions,
    ..._uliKeyOptions,
    ..._vlsOptions,
  ]);
}
